import { useState, useRef, useContext, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../utils/context";
import {
    checkQuota,
    analyzeDocument,
    sendChatMessage,
    getResearches,
    createChatSession,
    getChatSessions,
    deleteChatSession,
    deleteResearchItem,
    trackEvent
} from "../api/userApi";
import {
    Plus,
    FileText,
    MessageSquare,
    ArrowLeft,
    Trash2,
    Upload,
    X,
    Link as LinkIcon,
    ChevronRight,
    Send,
    Loader2,
    ShieldCheck,
    GraduationCap,
    PenTool,
    PanelLeftClose,
    PanelLeftOpen,
    LogOut,
    FlaskConical,
    FileSearch,
    Sparkles,
    Paperclip,
    Layout,
    CheckCircle2,
    Target,
    Zap,
    Scale,
    AlertCircle,
    UserCircle
} from "lucide-react";

const AGENT_CONFIG = {
    "summarizer": {
        title: "Paper Summarizer",
        icon: FileSearch,
        startMode: "upload",
        theme: { accent: "text-indigo-400", bg: "bg-indigo-500/10", button: "from-indigo-600 to-indigo-700" },
        placeholder: "Ask about the findings...",
        autoStart: false
    },
    "defense-paper": {
        title: "Defend Your Paper",
        icon: ShieldCheck,
        startMode: "upload",
        theme: { accent: "text-emerald-400", bg: "bg-emerald-500/10", button: "from-emerald-600 to-emerald-700" },
        placeholder: "Challenge your findings...",
        autoStart: true
    },
    "defense-thesis": {
        title: "Defend Your Thesis",
        icon: GraduationCap,
        startMode: "chat",
        theme: { accent: "text-purple-400", bg: "bg-purple-500/10", button: "from-purple-600 to-purple-700" },
        placeholder: "Discuss your thesis proposal...",
        autoStart: true
    },
    "related-works": {
        title: "Writing Assistant",
        icon: PenTool,
        startMode: "chat",
        theme: { accent: "text-orange-400", bg: "bg-orange-500/10", button: "from-orange-600 to-orange-700" },
        placeholder: "Help me synthesize my related works...",
        autoStart: false
    },
    "experiment-designer": {
        title: "Experiment Designer",
        icon: FlaskConical,
        startMode: "chat",
        theme: { accent: "text-amber-400", bg: "bg-amber-500/10", button: "from-amber-600 to-amber-700" },
        placeholder: "Suggest experiments for these hypotheses...",
        autoStart: false
    },
    "paper-reviewer": {
        title: "Paper Reviewer",
        icon: FileSearch,
        startMode: "upload",
        theme: { accent: "text-rose-400", bg: "bg-rose-500/10", button: "from-rose-600 to-rose-700" },
        placeholder: "Review this draft critically...",
        autoStart: true
    }
};

export default function AgentPage() {
    const { user, logout } = useContext(UserContext);
    const { researchId, agentId } = useParams();
    const navigate = useNavigate();
    const config = AGENT_CONFIG[agentId] || AGENT_CONFIG["summarizer"];

    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [project, setProject] = useState(null);
    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [quotas, setQuotas] = useState({ report: 0, chat: 0 });
    const [isLoading, setIsLoading] = useState(true);

    const [chatSessions, setChatSessions] = useState([]);
    const [generalSessions, setGeneralSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [chatInput, setChatInput] = useState("");
    const [isSendingChat, setIsSendingChat] = useState(false);

    const [uploadMode, setUploadMode] = useState('file');
    const [files, setFiles] = useState([]);
    const [urlInput, setUrlInput] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [processProgress, setProcessProgress] = useState(0);

    const fileInputRef = useRef();
    const chatContainerRef = useRef();
    const chatInputRef = useRef();

    const handleCreateInitialSession = async (sid) => {
        const res = await createChatSession(user.uid, researchId, sid, "Fresh Session");
        if (res.status) {
            setChatSessions([res.data]);
            setGeneralSessions(prev => [res.data, ...prev]);
            setCurrentSessionId(res.data.id);
            if (config.autoStart && !selectedItem) triggerProvocation(sid, res.data.id);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!user?.uid || !researchId) return;
            setIsLoading(true);
            try {
                const researchesRes = await getResearches(user.uid);
                if (researchesRes.status) {
                    const currentProj = researchesRes.data.find(r => r.id === researchId);
                    if (currentProj) {
                        setProject(currentProj);
                        const filteredItems = (currentProj.items || []).filter(item =>
                            !item.agent_id || item.agent_id === agentId
                        );
                        setItems(filteredItems);
                    }
                }
                const rq = await checkQuota(user.uid, "report_generation");
                const cq = await checkQuota(user.uid, "chat_message");
                setQuotas({ report: rq.remaining, chat: cq.remaining });

                const genRes = await getChatSessions(user.uid, researchId, `agent-${agentId}`);
                if (genRes.status) setGeneralSessions(genRes.data);

            } catch (err) { console.error(err); } finally { setIsLoading(false); }
        };
        fetchData();
    }, [user, researchId, agentId]);

    useEffect(() => {
        if (!project) return;
        const storageId = selectedItem ? selectedItem.uuid : `agent-${agentId}`;
        const syncSessions = async () => {
            if (config.startMode === 'upload' && !selectedItem) {
                setChatSessions([]);
                setCurrentSessionId(null);
                return;
            }
            const response = await getChatSessions(user.uid, project.id, storageId);
            if (response.status && response.data.length > 0) {
                setChatSessions(response.data);
                setCurrentSessionId(response.data[0].id);
            } else if (config.startMode === 'chat' && !selectedItem) {
                handleCreateInitialSession(storageId);
            }
        };
        syncSessions();
    }, [selectedItem, project, agentId]);

    const triggerProvocation = async (itemUuid, sessionId) => {
        setIsSendingChat(true);
        const response = await sendChatMessage(user.uid, project.id, itemUuid, null, sessionId, agentId, true);
        if (response.status) {
            const msg = { role: "assistant", content: response.response, timestamp: new Date().toISOString() };
            setChatSessions(prev => prev.map(s => s.id === sessionId ? { ...s, messages: [msg] } : s));
            setGeneralSessions(prev => prev.map(s => s.id === sessionId ? { ...s, messages: [msg] } : s));
        }
        setIsSendingChat(false);
    };

    const handleProcess = async () => {
        if (quotas.report < 1) { alert("Quota exceeded"); return; }
        setIsProcessing(true);
        setProcessProgress(0);
        const intv = setInterval(() => setProcessProgress(p => p < 90 ? p + 10 : p), 500);
        try {
            const res = await analyzeDocument(user.uid, project.id, uploadMode === 'file' ? files[0] : null, uploadMode === 'url' ? urlInput : null, agentId);
            if (!res.status) throw new Error(res.message);
            const newItem = res.data;
            setItems(prev => [newItem, ...prev]);
            setSelectedItem(newItem);
            setIsUploadModalOpen(false);
            setFiles([]); setUrlInput("");
            if (config.autoStart) {
                setTimeout(async () => {
                    const sid_res = await createChatSession(user.uid, project.id, newItem.uuid, "Intelligence Analysis");
                    if (sid_res.status) {
                        setChatSessions([sid_res.data]);
                        setCurrentSessionId(sid_res.data.id);
                        triggerProvocation(newItem.uuid, sid_res.data.id);
                    }
                }, 500);
            }
        } catch (error) { alert(error.message); } finally { setIsProcessing(false); clearInterval(intv); }
    };

    const handleSendChat = async () => {
        if (!chatInput.trim() || isSendingChat || !currentSessionId) return;
        const message = chatInput;
        setChatInput("");
        setIsSendingChat(true);
        const storageId = selectedItem ? selectedItem.uuid : `agent-${agentId}`;
        const currentSess = chatSessions.find(s => s.id === currentSessionId);
        const newMessages = [...(currentSess?.messages || []), { role: "user", content: message, timestamp: new Date().toISOString() }];
        setChatSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: newMessages } : s));
        if (!selectedItem) setGeneralSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: newMessages } : s));
        const response = await sendChatMessage(user.uid, project.id, storageId, message, currentSessionId, agentId);
        if (response.status) {
            const msg = { role: "assistant", content: response.response, timestamp: new Date().toISOString() };
            const finalMsgs = [...newMessages, msg];
            setChatSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: finalMsgs } : s));
            if (!selectedItem) setGeneralSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: finalMsgs } : s));
        }
        setIsSendingChat(false);
    };

    const currentMessages = useMemo(() => chatSessions.find(s => s.id === currentSessionId)?.messages || [], [chatSessions, currentSessionId]);

    const scrollToBottom = () => { if (chatContainerRef.current) chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight; };
    useEffect(() => { scrollToBottom(); }, [chatSessions, currentSessionId]);

    const handleNewConversation = () => {
        setSelectedItem(null);
        setChatSessions([]);
        setCurrentSessionId(null);
        if (config.startMode === 'chat') {
            handleCreateInitialSession(`agent-${agentId}`);
        }
    };

    const handleSelectGeneralSession = (session) => {
        setSelectedItem(null);
        setChatSessions([session]);
        setCurrentSessionId(session.id);
    };

    const getSessionQuickTitle = (session) => {
        const firstUserMsg = session.messages?.find(m => m.role === 'user');
        if (firstUserMsg && firstUserMsg.content) {
            const content = firstUserMsg.content.trim();
            return content.length > 22 ? content.slice(0, 19) + "..." : content;
        }
        const firstAssistantMsg = session.messages?.find(m => m.role === 'assistant');
        if (firstAssistantMsg && firstAssistantMsg.content && firstAssistantMsg.content.length < 100) {
            const content = firstAssistantMsg.content.trim();
            return content.length > 22 ? content.slice(0, 19) + "..." : content;
        }
        return "New Consultation";
    };

    return (
        <div className="flex h-screen bg-[#0f172a] text-white overflow-hidden font-sans">
            {/* Sidebar */}
            <AnimatePresence>
                {isSidebarOpen && (
                    <motion.aside initial={{ width: 0 }} animate={{ width: 280 }} exit={{ width: 0 }} className="flex-shrink-0 bg-[#0a0f1d] border-r border-white/5 flex flex-col">
                        <div className="p-4 border-b border-white/5 space-y-4">
                            <button onClick={() => navigate(`/research/${researchId}/dashboard`)} className="flex items-center gap-2 text-white/40 hover:text-white text-xs"><ArrowLeft size={14} /> Back</button>
                            <button onClick={handleNewConversation} className="w-full bg-indigo-600 hover:bg-indigo-500 py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-bold shadow-lg shadow-indigo-600/20 transition-all active:scale-95"><Plus size={18} /> {config.startMode === 'chat' ? 'New Chat' : 'New Analysis'}</button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-3 space-y-6">
                            {/* IF upload-first, show ONLY Analysis History */}
                            {config.startMode === 'upload' ? (
                                <div>
                                    <h4 className="px-3 text-[10px] font-black uppercase tracking-widest text-white/20 mb-2">Analysis History</h4>
                                    <div className="space-y-1">
                                        {items.map(item => (
                                            <div key={item.uuid} onClick={() => setSelectedItem(item)} className={`p-3 rounded-xl cursor-pointer flex items-center gap-3 group relative ${selectedItem?.uuid === item.uuid ? 'bg-white/10 text-white' : 'text-white/40 hover:bg-white/5'}`}>
                                                <FileText size={16} /> <span className="truncate text-sm font-semibold">{item.title}</span>
                                                <button onClick={async e => { e.stopPropagation(); if (window.confirm("Delete?")) { await deleteResearchItem(user.uid, researchId, item.uuid); setItems(prev => prev.filter(i => i.uuid !== item.uuid)); if (selectedItem?.uuid === item.uuid) setSelectedItem(null); } }} className="absolute right-2 opacity-0 group-hover:opacity-100 text-white/20 hover:text-red-400 transition-all"><Trash2 size={14} /></button>
                                            </div>
                                        ))}
                                        {items.length === 0 && <div className="px-4 py-4 text-center text-white/5 text-[10px] italic">No analyses yet</div>}
                                    </div>
                                </div>
                            ) : (
                                /* IF chat-first, show ONLY Chat History */
                                <div>
                                    <h4 className="px-3 text-[10px] font-black uppercase tracking-widest text-white/20 mb-2">Chat History</h4>
                                    <div className="space-y-1">
                                        {generalSessions.map(session => (
                                            <div key={session.id} onClick={() => handleSelectGeneralSession(session)} className={`p-3 rounded-xl cursor-pointer flex items-center gap-3 group relative ${!selectedItem && currentSessionId === session.id ? 'bg-white/10 text-white' : 'text-white/40 hover:bg-white/5'}`}>
                                                <MessageSquare size={16} /> <span className="truncate text-sm font-semibold tracking-tight">{getSessionQuickTitle(session)}</span>
                                                <button onClick={async e => { e.stopPropagation(); if (window.confirm("Delete?")) { await deleteChatSession(user.uid, researchId, session.id); setGeneralSessions(prev => prev.filter(s => s.id !== session.id)); if (currentSessionId === session.id) handleNewConversation(); } }} className="absolute right-2 opacity-0 group-hover:opacity-100 text-white/20 hover:text-red-400 transition-all"><Trash2 size={14} /></button>
                                            </div>
                                        ))}
                                        {generalSessions.length === 0 && <div className="px-4 py-4 text-center text-white/5 text-[10px] italic">No chats yet</div>}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t border-white/5 bg-white/[0.02] flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-xs font-bold">{user?.email?.[0].toUpperCase()}</div>
                                <span className="text-[10px] font-medium text-white/40 truncate max-w-[120px]">{user?.email}</span>
                            </div>
                            <button onClick={logout} className="p-2 text-white/20 hover:text-white transition-colors"><LogOut size={16} /></button>
                        </div>
                    </motion.aside>
                )}
            </AnimatePresence>

            <main className="flex-1 flex flex-col relative bg-[#0f172a]">
                <header className="h-14 border-b border-white/5 flex items-center px-4 justify-between backdrop-blur-md">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 hover:bg-white/5 text-white/40 rounded-lg">{isSidebarOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}</button>
                        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10">
                            <config.icon size={16} className={config.theme.accent} /> <span className="text-xs font-bold">{config.title}</span>
                        </div>
                    </div>
                </header>

                <div className="flex-1 relative flex flex-col overflow-hidden">
                    {!selectedItem && !currentSessionId && config.startMode === 'upload' && !isProcessing && (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gradient-to-b from-indigo-500/5 to-transparent">
                            <div className={`w-24 h-24 ${config.theme.bg} rounded-[2rem] flex items-center justify-center ${config.theme.accent} mb-8 shadow-2xl`}><Upload size={48} /></div>
                            <h2 className="text-4xl font-black mb-4 tracking-tighter">Ready for analysis?</h2>
                            <p className="text-white/40 max-w-sm mb-12 text-base leading-relaxed">Upload a paper or paste a URL to begin the <b>{config.title}</b> protocol.</p>
                            <div className="max-w-md w-full space-y-8">
                                <div className="flex bg-white/5 p-1.5 rounded-2xl"><button onClick={() => setUploadMode('file')} className={`flex-1 py-3.5 rounded-xl text-xs font-bold transition-all ${uploadMode === 'file' ? 'bg-white/10 text-white shadow-xl' : 'text-white/40 hover:text-white/60'}`}>Upload PDF</button><button onClick={() => setUploadMode('url')} className={`flex-1 py-3.5 rounded-xl text-xs font-bold transition-all ${uploadMode === 'url' ? 'bg-white/10 text-white shadow-xl' : 'text-white/40 hover:text-white/60'}`}>Paste URL</button></div>
                                {uploadMode === 'file' ? <div onClick={() => fileInputRef.current.click()} className="group border-2 border-dashed border-white/10 rounded-[2.5rem] p-16 hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all cursor-pointer"><Upload size={40} className="mx-auto mb-4 text-white/20 group-hover:text-indigo-400 group-hover:scale-110 transition-all" /><p className="text-sm font-bold text-white/30 group-hover:text-white/60">{files[0]?.name || "Select PDF"}</p><input type="file" ref={fileInputRef} className="hidden" accept=".pdf" onChange={e => setFiles(Array.from(e.target.files))} /></div> : <input type="text" value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="https://..." className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-6 text-sm outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" />}
                                <button onClick={handleProcess} className={`w-full bg-gradient-to-r ${config.theme.button} py-5 rounded-2xl font-black text-sm uppercase tracking-widest shadow-2xl active:scale-95 transition-all`}>Start Analysis</button>
                            </div>
                        </div>
                    )}

                    {(selectedItem || currentSessionId) && (
                        <div className="flex-1 flex flex-col overflow-hidden">
                            <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-4 py-8 space-y-8">
                                <div className="max-w-4xl mx-auto w-full space-y-12 pb-32">
                                    {selectedItem && (
                                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.03] border border-white/10 rounded-[2.5rem] p-10 backdrop-blur-xl relative overflow-hidden">
                                            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-[100px] pointer-events-none"></div>
                                            <div className="relative z-10">
                                                <div className="flex items-start justify-between gap-6 mb-12">
                                                    <div className="flex items-center gap-5">
                                                        <div className={`w-14 h-14 ${config.theme.bg} rounded-2xl flex items-center justify-center ${config.theme.accent} shadow-2xl`}><FileText size={28} /></div>
                                                        <div><div className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400 mb-1">Intelligence Protocol Active</div><h3 className="text-xl font-black text-white leading-tight">{selectedItem.title}</h3></div>
                                                    </div>
                                                    <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/40 text-[10px] font-black uppercase tracking-widest h-fit">REF: {selectedItem.uuid?.slice(0, 8)}</div>
                                                </div>
                                                <div className="space-y-10">
                                                    <section>
                                                        <div className="flex items-center gap-2 mb-4 px-2"><Layout size={14} className="text-white/20" /><div className="text-[10px] font-black uppercase tracking-[0.3em] text-white/20">Executive Summary</div></div>
                                                        <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 text-sm text-white/60 leading-relaxed font-medium">{selectedItem.content?.summary}</div>
                                                    </section>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <section>
                                                            <div className="flex items-center gap-2 mb-4 px-2"><Target size={14} className="text-indigo-400/40" /><div className="text-[10px] font-black uppercase tracking-[0.3em] text-white/20">Key Findings</div></div>
                                                            <div className="bg-indigo-500/5 border border-indigo-500/10 rounded-3xl p-6 space-y-4">
                                                                {selectedItem.content?.keyFindings?.map((f, i) => (<div key={i} className="flex gap-3 text-sm text-indigo-100/70 font-medium"><div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0" />{f}</div>))}
                                                            </div>
                                                        </section>
                                                        <section>
                                                            <div className="flex items-center gap-2 mb-4 px-2"><Zap size={14} className="text-emerald-400/40" /><div className="text-[10px] font-black uppercase tracking-[0.3em] text-white/20">Methodology Frame</div></div>
                                                            <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-3xl p-6 text-sm text-emerald-100/70 font-medium">{selectedItem.content?.methodology}</div>
                                                        </section>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {!selectedItem && currentSessionId && (
                                        <div className="flex flex-col items-center justify-center py-12 border-b border-white/5 mb-12">
                                            <div className={`w-16 h-16 ${config.theme.bg} rounded-2xl flex items-center justify-center ${config.theme.accent} mb-4 shadow-xl`}><MessageSquare size={32} /></div>
                                            <h2 className="text-2xl font-black">{config.title} Consultation</h2>
                                            <p className="text-white/20 text-xs font-bold uppercase tracking-widest mt-1">Specialized Brainstorming Session</p>
                                        </div>
                                    )}

                                    {currentMessages.map((msg, idx) => (
                                        <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[85%] px-6 py-5 rounded-[2rem] text-sm leading-relaxed ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-none shadow-xl shadow-indigo-600/20' : 'bg-white/5 text-white/80 border border-white/10 rounded-tl-none'}`}>{msg.content}</div>
                                        </motion.div>
                                    ))}
                                    {isSendingChat && <div className="flex justify-start"><div className="bg-white/5 p-4 rounded-2xl animate-pulse"><Loader2 size={20} className="animate-spin text-white/20" /></div></div>}
                                </div>
                            </div>
                            <div className="p-4 md:p-8 bg-[#0f172a]/80 backdrop-blur-xl border-t border-white/5 sticky bottom-0">
                                <div className="max-w-3xl mx-auto relative flex items-end gap-3">
                                    <button onClick={() => setIsUploadModalOpen(true)} className="p-5 bg-white/5 border border-white/10 rounded-[1.5rem] text-white/30 hover:text-white transition-all hover:bg-white/10" title="Link context"><Paperclip size={24} /></button>
                                    <div className="flex-1 relative bg-white/5 border border-white/10 focus-within:border-indigo-500/50 rounded-[1.5rem] p-2 flex items-end gap-2 transition-all">
                                        <textarea ref={chatInputRef} rows={1} value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }} placeholder={config.placeholder} className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-4 px-4 resize-none max-h-48 text-white/80" />
                                        <button onClick={handleSendChat} disabled={!chatInput.trim() || isSendingChat} className="p-5 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-500 transition-all disabled:opacity-20 shadow-2xl shadow-indigo-600/20"><Send size={24} /></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <AnimatePresence>
                    {isUploadModalOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0a0f1d]/95 backdrop-blur-md">
                            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-[#0f172a] border border-white/10 rounded-[3rem] p-10 max-w-lg w-full relative shadow-[0_0_100px_rgba(79,70,229,0.15)]">
                                <button onClick={() => setIsUploadModalOpen(false)} className="absolute top-8 right-8 text-white/20 hover:text-white transition-colors"><X size={28} /></button>
                                <h3 className="text-2xl font-black mb-2">Inject Context</h3>
                                <p className="text-sm text-white/40 mb-10 font-medium">Add a document to inform the current session.</p>
                                <div className="space-y-8">
                                    <div className="flex bg-white/5 p-1.5 rounded-2xl"><button onClick={() => setUploadMode('file')} className={`flex-1 py-3 rounded-xl text-xs font-bold transition-all ${uploadMode === 'file' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}>PDF DOCUMENT</button><button onClick={() => setUploadMode('url')} className={`flex-1 py-3 rounded-xl text-xs font-bold transition-all ${uploadMode === 'url' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}>URL REFERENCE</button></div>
                                    {uploadMode === 'file' ? <div onClick={() => fileInputRef.current.click()} className="group border-2 border-dashed border-white/10 rounded-[2rem] p-12 text-center cursor-pointer hover:border-indigo-500/30 bg-white/[0.02] transition-all"><Upload className="mx-auto mb-4 text-white/20 group-hover:text-indigo-400 group-hover:scale-110 transition-all" /><p className="text-sm font-bold text-white/30">{files[0]?.name || "Select PDF"}</p><input type="file" ref={fileInputRef} className="hidden" accept=".pdf" onChange={e => setFiles(Array.from(e.target.files))} /></div> : <input type="text" value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="Paste link here..." className="w-full bg-white/5 border border-white/10 p-5 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" />}
                                    <button onClick={handleProcess} disabled={isProcessing} className="w-full bg-indigo-600 py-5 rounded-2xl font-black text-sm tracking-widest uppercase shadow-2xl hover:bg-indigo-500 transition-all active:scale-95">Analyze & Attach</button>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {isProcessing && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-[#0a0f1d]/90 backdrop-blur-2xl flex flex-col items-center justify-center">
                            <div className="relative w-32 h-32 mb-10">
                                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 3, ease: "linear" }} className="absolute inset-0 border-[6px] border-indigo-500/10 border-t-indigo-500 rounded-full shadow-[0_0_40px_rgba(79,70,229,0.3)]" />
                                <div className="absolute inset-0 flex items-center justify-center"><Loader2 size={40} className="animate-spin text-white/50" /></div>
                            </div>
                            <h3 className="text-2xl font-black mb-2 uppercase tracking-[0.2em] text-indigo-400">Processing Protocol</h3>
                            <p className="text-white/40 font-black text-sm tracking-[0.1em]">{processProgress}% COMPLETE</p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
}
